import { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import type { Profile } from '../../types/profile';

const motivationalQuotes = [
  "Every session is a step toward healing.",
  "Your dedication makes a difference.",
  "Supporting others is powerful work.",
  "Progress, not perfection.",
  "You're making an impact today.",
  "Small moments create big change.",
  "Your empathy is your strength.",
  "You help others find their light.",
  "Healing starts with listening.",
  "Each client leaves a little stronger.",
  "You're guiding growth, one conversation at a time.",
  "Compassion is your superpower.",
  "Every note you write tells a story of hope.",
  "You make hard days a little easier for others.",
  "Keep showing up — it matters more than you know.",
  "Even quiet progress is progress.",
  "You bring calm where there was chaos.",
  "Your work plants seeds of resilience.",
  "The care you give ripples outward.",
  "You’re doing good work. Keep going.",
  "You hold space where healing begins.",
  "Your presence is sometimes the best intervention.",
  "Progress can be invisible — but it’s happening.",
  "You’re helping people find their footing again.",
  "The smallest kindness can change a life.",
  "You help others rewrite their stories.",
  "Every deep breath counts as progress.",
  "You bring structure to someone’s storm.",
  "Change takes time — and you make that time matter.",
  "Even listening is an act of healing.",
  "Your work helps rebuild confidence.",
  "What you do matters, even on quiet days.",
  "You help people remember their strength.",
  "Growth looks different for everyone — and that’s okay.",
  "Healing isn’t linear — but your care keeps it moving forward.",
  "Your calm helps others find theirs.",
  "Sometimes, showing up is enough.",
  "You help turn struggle into strength.",
  "Hope grows in the presence of patience.",
  "You’re a steady hand in someone’s storm.",
  "Empathy is contagious — and you spread it daily.",
  "You’re making a difference, one note at a time.",
  "Kindness is never wasted.",
  "You remind people that healing is possible.",
  "You are part of someone’s turning point.",
  "Progress doesn’t always shout — sometimes it whispers.",
  "You bring light into difficult places.",
  "Your work is quiet heroism.",
  "Healing happens in the small moments too.",
  "Every conversation you have could change a life.",
  "You help people see they’re not alone.",
  "What you do today could echo for years.",
  "The care you give helps people rebuild trust.",
  "You are helping people find peace within themselves.",
  "Even when it feels routine, your work is sacred.",
  "You help people rediscover who they are.",
  "Your support makes growth possible.",
  "Healing doesn’t happen overnight — but you stay through every night.",
  "Your compassion shapes recovery.",
  "You’re doing meaningful work, even when it’s hard.",
];


export function WelcomeBanner() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(() => {
    // Try to load cached profile immediately
    if (!user) return null;
    try {
      const cached = localStorage.getItem(`profile_${user.id}`);
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });

  const [quote] = useState(() => {
    // Get today's date as a string for the cache key
    const today = new Date();
    const dateKey = `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`;
    const storageKey = `daily_quote_${dateKey}`;

    // Check if we have a quote cached for today
    const cachedQuote = localStorage.getItem(storageKey);
    if (cachedQuote) {
      return cachedQuote;
    }

    // Generate a new quote and cache it
    const index = Math.floor(Math.random() * motivationalQuotes.length);
    const newQuote = motivationalQuotes[index];
    localStorage.setItem(storageKey, newQuote);

    // Clean up old quotes from localStorage (keep only today's)
    try {
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('daily_quote_') && key !== storageKey) {
          localStorage.removeItem(key);
        }
      });
    } catch (e) {
      // Ignore cleanup errors
    }

    return newQuote;
  });

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;

      const { data } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      if (data) {
        setProfile(data);
        // Cache the profile in localStorage
        try {
          localStorage.setItem(`profile_${user.id}`, JSON.stringify(data));
        } catch (e) {
          // Ignore cache errors
        }
      }
    };

    fetchProfile();
  }, [user]);

  const now = new Date();
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayName = days[now.getDay()];
  const monthName = months[now.getMonth()];
  const date = now.getDate();
  const year = now.getFullYear();

  const firstName = profile?.first_name || 'there';

  return (
    <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg p-6 md:p-8 text-white shadow-lg">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-5 h-5" />
            <p className="text-sm font-medium text-blue-100">
              {dayName}, {monthName} {date}, {year}
            </p>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-2">
            Welcome back, {firstName}!
          </h1>
          <p className="text-blue-100 text-lg">
            {quote}
          </p>
        </div>
      </div>
    </div>
  );
}
